import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Stack, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getMafertAppModules, getMafertAppModule } from '../api/mafertappModulesApi';
import { colorTokens } from '../app/designTokens';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { DataTable } from '../components/DataTable';
import { DetailField } from '../components/InfoCard';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { IconButton } from '../components/ui/IconButton';

const { semantic } = colorTokens;

const ACCESS_CATEGORY_TONE = {
  core: 'Brand',
  paid_module: 'Success',
  future_override: 'Warning',
  support_dual_flow: 'Info',
};

const detailGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
  gap: 2.5,
  minWidth: 0,
  maxWidth: '100%',
  '& > *': {
    minWidth: 0,
    maxWidth: '100%',
  },
};

const monoSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 12,
  lineHeight: '18px',
  color: semantic.text.primary,
  minWidth: 0,
  maxWidth: '100%',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

function categoryLabel(t, category) {
  const map = {
    core: t('mafertappModules:coreModule'),
    paid_module: t('mafertappModules:paidModule'),
    future_override: t('mafertappModules:futureOverride'),
    support_dual_flow: t('mafertappModules:supportDualFlow'),
  };
  return map[category] || category;
}

function AccessCategoryBadge({ category, t }) {
  if (!category) return null;
  return (
    <StatusBadge
      status={category}
      category="generic"
      apiLabel={categoryLabel(t, category)}
      size="Sm"
      tone={ACCESS_CATEGORY_TONE[category] || 'Neutral'}
    />
  );
}

function YesNoBadge({ value, t }) {
  return (
    <StatusBadge
      status={value ? 'yes' : 'no'}
      category="generic"
      apiLabel={value ? t('common:yes') : t('common:no')}
      size="Sm"
      tone={value ? 'Success' : 'Neutral'}
    />
  );
}

export function MafertAppModulesPage() {
  const { t } = useTranslation(['mafertappModules', 'common']);
  const navigate = useNavigate();
  const { session } = useAuth();
  const canView = hasPermission(session, 'system.mafertapp_modules.view');

  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const emptyValue = t('common:empty_value');
  const recordCount = modules.length;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await getMafertAppModules();
      setModules(rows);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canView) {
      load();
    } else {
      setLoading(false);
    }
  }, [canView, load]);

  const columns = useMemo(
    () => [
      {
        id: 'customer_name_sk',
        label: t('mafertappModules:columns.customer_name'),
        render: (row) => row.customer_name_sk || emptyValue,
      },
      {
        id: 'billing_code',
        label: t('mafertappModules:billingCode'),
        render: (row) => row.billing_code || emptyValue,
      },
      {
        id: 'mafertapp_parent',
        label: t('mafertappModules:parentModule'),
        render: (row) => row.mafertapp_parent || emptyValue,
      },
      {
        id: 'access_category',
        label: t('mafertappModules:accessCategory'),
        render: (row) => (
          row.access_category
            ? <AccessCategoryBadge category={row.access_category} t={t} />
            : emptyValue
        ),
      },
      {
        id: 'seed_scope',
        label: t('mafertappModules:seedScope'),
        render: (row) => row.seed_scope || emptyValue,
      },
      {
        id: 'permission_count',
        label: t('mafertappModules:permissionCount'),
        render: (row) => (
          row.permission_count === 0 || row.permission_count
            ? row.permission_count
            : emptyValue
        ),
      },
      {
        id: 'public_pricing',
        label: t('mafertappModules:columns.public_pricing'),
        render: (row) => <YesNoBadge value={Boolean(row.public_pricing)} t={t} />,
      },
      {
        id: 'actions',
        label: t('common:actions'),
        align: 'right',
        width: 72,
        render: (row) => (
          <IconButton
            size="Sm"
            tone="Neutral"
            aria-label={t('common:view_detail')}
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/system-modules/mafertapp/${row.id}`);
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        ),
      },
    ],
    [emptyValue, navigate, t],
  );

  if (!canView) {
    return <Alert severity="warning">{t('common:forbidden')}</Alert>;
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('mafertappModules:title')}
        subtitle={t('mafertappModules:description')}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('common:load_failed')}
        </Alert>
      )}

      <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={t('mafertappModules:title')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('mafertappModules:toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton
            type="Table"
            embedded
            rows={3}
            columns={columns.length}
          />
        ) : (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={modules}
            getRowKey={(row) => row.id}
            emptyMessage={t('mafertappModules:empty_list')}
            onRowClick={(row) => navigate(`/system-modules/mafertapp/${row.id}`)}
            sx={{
              overflowX: 'auto',
              width: '100%',
              '& table': {
                width: '100%',
                minWidth: '100%',
              },
            }}
          />
        )}
      </SectionCard>
    </Box>
  );
}

export function MafertAppModuleDetailPage({ moduleId }) {
  const { t } = useTranslation(['mafertappModules', 'common']);
  const { session } = useAuth();
  const canView = hasPermission(session, 'system.mafertapp_modules.view');
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const emptyValue = t('common:empty_value');

  const load = useCallback(async () => {
    if (!moduleId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getMafertAppModule(moduleId);
      setModule(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [moduleId]);

  useEffect(() => {
    if (canView) {
      load();
    } else {
      setLoading(false);
    }
  }, [canView, load]);

  const permissionColumns = useMemo(
    () => [
      {
        id: 'label',
        label: t('common:name'),
        render: (row) => row.label_key || emptyValue,
      },
      {
        id: 'view',
        label: 'view',
        render: (row) => (
          <Typography sx={monoSx}>{row.permissions?.view || emptyValue}</Typography>
        ),
      },
      {
        id: 'add',
        label: 'add',
        render: (row) => (
          <Typography sx={monoSx}>{row.permissions?.add || emptyValue}</Typography>
        ),
      },
      {
        id: 'edit',
        label: 'edit',
        render: (row) => (
          <Typography sx={monoSx}>{row.permissions?.edit || emptyValue}</Typography>
        ),
      },
      {
        id: 'delete',
        label: 'delete',
        render: (row) => (
          <Typography sx={monoSx}>{row.permissions?.delete || emptyValue}</Typography>
        ),
      },
    ],
    [emptyValue, t],
  );

  if (!canView) {
    return <Alert severity="warning">{t('common:forbidden')}</Alert>;
  }

  if (loading) {
    return <LoadingSkeleton type="Detail" />;
  }

  if (error || !module) {
    return (
      <SectionCard variant="Danger">
        <Alert severity="error" sx={{ bgcolor: 'transparent', p: 0 }}>
          {t('common:load_failed')}
        </Alert>
      </SectionCard>
    );
  }

  const permissionRows = module.permission_mappings || [];

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="Detail"
        title={module.customer_name_sk}
        subtitle={t('mafertappModules:detail_title')}
        backTo="/system-modules/mafertapp"
        badge={(
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            {module.access_category && (
              <AccessCategoryBadge category={module.access_category} t={t} />
            )}
            {module.billing_status && (
              <StatusBadge
                status={module.billing_status}
                category="generic"
                apiLabel={module.billing_status}
                size="Md"
              />
            )}
          </Stack>
        )}
      />

      <SectionCard variant="Default" sx={{ mb: 2.5 }}>
        <Box sx={detailGridSx}>
          <DetailField
            label={t('mafertappModules:billingCode')}
            value={module.billing_code || emptyValue}
          />
          <DetailField
            label={t('mafertappModules:parentModule')}
            value={module.mafertapp_parent || emptyValue}
          />
          <DetailField label={t('mafertappModules:accessCategory')}>
            {module.access_category
              ? <AccessCategoryBadge category={module.access_category} t={t} />
              : emptyValue}
          </DetailField>
          <DetailField label={t('common:status')}>
            {module.billing_status
              ? (
                <StatusBadge
                  status={module.billing_status}
                  category="generic"
                  apiLabel={module.billing_status}
                  size="Sm"
                />
              )
              : emptyValue}
          </DetailField>
          <DetailField label={t('mafertappModules:columns.public_pricing')}>
            <YesNoBadge value={Boolean(module.public_pricing)} t={t} />
          </DetailField>
          <DetailField
            label={t('mafertappModules:permissionCount')}
            value={
              module.permission_count === 0 || module.permission_count
                ? module.permission_count
                : emptyValue
            }
          />
          {module.legacy_code && (
            <DetailField label={t('mafertappModules:legacyName')}>
              <Typography sx={monoSx}>{module.legacy_code}</Typography>
            </DetailField>
          )}
        </Box>
        {module.notes && (
          <Box sx={{ mt: 2.5 }}>
            <DetailField label={t('mafertappModules:risks')} value={module.notes} />
          </Box>
        )}
      </SectionCard>

      {module.seed_policy && (
        <SectionCard variant="Default" title={t('mafertappModules:seedPolicy')} sx={{ mb: 2.5 }}>
          <Box sx={detailGridSx}>
            {module.seed_policy.seed_category && (
              <DetailField label="seed_category">
                <Typography sx={monoSx}>{module.seed_policy.seed_category}</Typography>
              </DetailField>
            )}
            {module.seed_policy.seed_type && (
              <DetailField label="seed_type">
                <Typography sx={monoSx}>{module.seed_policy.seed_type}</Typography>
              </DetailField>
            )}
            {!module.seed_policy.seed_category && !module.seed_policy.seed_type && (
              <DetailField label={t('mafertappModules:seedScope')} value={emptyValue} />
            )}
          </Box>
          {module.seed_policy.notes && (
            <Box sx={{ mt: 2.5 }}>
              <DetailField label={t('mafertappModules:risks')} value={module.seed_policy.notes} />
            </Box>
          )}
        </SectionCard>
      )}

      <SectionCard
        variant="Default"
        title={t('mafertappModules:technicalPermissions')}
        noPadding
        sx={{ mb: 0 }}
      >
        <DataTable
          type="Technical"
          embedded
          columns={permissionColumns}
          rows={permissionRows}
          getRowKey={(row) => row.id}
          emptyMessage={t('mafertappModules:no_modules')}
          sx={{
            overflowX: 'auto',
            width: '100%',
            '& table': {
              width: '100%',
              minWidth: 720,
            },
          }}
        />
      </SectionCard>
    </Box>
  );
}
